// Delete Role dialog — confirmation with blocker feedback.
// Shows what prevents deletion (members, grants, composite parents) when the backend returns 409.

import { useState } from 'react'
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog'
import { ApiRequestError } from '@/lib/api/client'

interface DeleteBlockers {
  active_members: number
  active_grants: number
  composite_parents: string[]
}

interface DeleteRoleDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  roleName: string
  onConfirm: () => Promise<void>
}

export function DeleteRoleDialog({ open, onOpenChange, roleName, onConfirm }: DeleteRoleDialogProps) {
  const [deleting, setDeleting] = useState(false)
  const [blockers, setBlockers] = useState<DeleteBlockers | null>(null)

  async function handleDelete() {
    setDeleting(true)
    setBlockers(null)
    try {
      await onConfirm()
      onOpenChange(false)
    } catch (err) {
      if (err instanceof ApiRequestError && err.status === 409) {
        // Parse blocker info from response
        try {
          const body = err.apiError as unknown as { blockers?: DeleteBlockers }
          if (body?.blockers) {
            setBlockers(body.blockers)
          }
        } catch {
          // Couldn't parse blockers — just show generic message
        }
      }
    } finally {
      setDeleting(false)
    }
  }

  function handleClose(isOpen: boolean) {
    if (!isOpen) setBlockers(null)
    onOpenChange(isOpen)
  }

  return (
    <AlertDialog open={open} onOpenChange={handleClose}>
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>Delete role</AlertDialogTitle>
          <AlertDialogDescription>
            {blockers ? (
              <span className="space-y-2">
                <span className="block">
                  <strong>{roleName}</strong> cannot be deleted because it has:
                </span>
                <span className="block">
                  {blockers.active_members > 0 && (
                    <span className="block text-sm">
                      • {blockers.active_members} assigned member{blockers.active_members !== 1 ? 's' : ''}
                    </span>
                  )}
                  {blockers.active_grants > 0 && (
                    <span className="block text-sm">
                      • {blockers.active_grants} permission grant{blockers.active_grants !== 1 ? 's' : ''}
                    </span>
                  )}
                  {blockers.composite_parents.length > 0 && (
                    <span className="block text-sm">
                      • Included in composite role{blockers.composite_parents.length !== 1 ? 's' : ''}:{' '}
                      {blockers.composite_parents.join(', ')}
                    </span>
                  )}
                </span>
                <span className="block text-sm">
                  Remove these associations before deleting the role.
                </span>
              </span>
            ) : (
              <>
                Are you sure you want to delete <strong>{roleName}</strong>? This action cannot be undone.
              </>
            )}
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel disabled={deleting}>
            {blockers ? 'Close' : 'Cancel'}
          </AlertDialogCancel>
          {!blockers && (
            <AlertDialogAction
              onClick={handleDelete}
              disabled={deleting}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              {deleting ? 'Deleting...' : 'Delete'}
            </AlertDialogAction>
          )}
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  )
}
